import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, ArrowLeft, Check, RotateCcw } from 'lucide-react';
import { evaluateDecisionFE, getDecisionPresentation } from '@/utils/decisionState';
import { trackInitiateCheckout, trackViewContent } from '@/lib/metaPixel';
import { PACK_PRICE_ARS, PACK_ENTREVISTAS, formatPriceARS } from '@/config/pricing';
import {
  FUENTE_TITULOS,
  FUENTE_ROTULO,
  FUENTE_TEXTO,
  RESALTADO,
  BTN_PRIMARY,
  BTN_SECUNDARIO,
  Papel,
  Mancha,
  GRANO,
} from '@/components/Collage';

// ---------------------------------------------------------------------------
// /probar: destino del anuncio "¿Con qué intensidad real entrena tu alumno?"
// (31/10/2026). Tres preguntas y el resultado al instante, sin pedir
// mail. Usa el MISMO motor que la entrevista completa (evaluateDecisionFE,
// espejo de calculations.py) y las mismas preguntas del cuestionario: el
// resultado es el que daría EVALPRO, no una versión inventada. Abajo del
// resultado, la oferta del pack. No guarda nada.
// ---------------------------------------------------------------------------

// Mismos textos que el paso de signos y síntomas de la entrevista
// (NewEvaluationPage), en versión corta para elegir de una lista.
const SINTOMAS = [
  ['symptom_chest_pain', 'Dolor o malestar en el pecho, en reposo o con actividad'],
  ['symptom_neck_jaw_arm_pain', 'Dolor o molestia en cuello, mandíbula o brazos'],
  ['symptom_dyspnea', 'Falta de aire inusual o con esfuerzos leves'],
  ['symptom_dizziness', 'Mareos o pérdida de conciencia durante la actividad'],
  ['symptom_orthopnea', 'Falta de aire al estar acostado o de noche'],
  ['symptom_ankle_swelling', 'Hinchazón persistente en los tobillos'],
  ['symptom_palpitations', 'Palpitaciones o latidos irregulares frecuentes'],
  ['symptom_intermittent_claudication', 'Dolor en las piernas al caminar que mejora con reposo'],
  ['symptom_heart_murmur', 'Le informaron un soplo cardíaco'],
  ['symptom_unusual_fatigue', 'Fatiga inusual con actividades habituales'],
];

// Las enfermedades que cuentan para el motor (ACSM). HTA y colesterol no
// cambian el escenario: se registran en la entrevista completa.
const ENFERMEDADES = [
  ['diabetes', 'Diabetes'],
  ['cardiac_disease', 'Enfermedad cardíaca'],
  ['cerebrovascular_disease', 'ACV o AIT previo'],
  ['peripheral_artery_disease', 'Enfermedad arterial periférica o aneurisma de aorta'],
  ['chronic_kidney_disease', 'Enfermedad renal crónica'],
];

// Color de cada escenario, igual que en la landing.
const COLOR_ESCENARIO = {
  verde: '#27AE60',
  amarillo_claro: '#F4C430',
  amarillo: '#D97706',
  naranja: '#EA580C',
  rojo: '#C83A2A',
};

function Opcion({ activa, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activa}
      data-testid={testId}
      className={`w-full flex items-start gap-3 text-left rounded-xl border-2 px-4 py-3 text-[15px] leading-snug transition-colors ${
        activa
          ? 'border-[#14110d] bg-[#27AE60]/25 text-[#14110d]'
          : 'border-[#14110d]/25 bg-white/30 text-[#2a241b] hover:border-[#14110d]/60'
      }`}
      style={{ flexWrap: 'nowrap' }}
    >
      <span
        className={`mt-0.5 h-5 w-5 flex-shrink-0 flex items-center justify-center border-2 border-[#14110d] ${
          activa ? 'bg-[#27AE60]' : 'bg-transparent'
        }`}
      >
        {activa && <Check className="h-3.5 w-3.5 text-[#14110d]" strokeWidth={4} />}
      </span>
      <span>{children}</span>
    </button>
  );
}

function Cabecera({ paso, titulo, ayuda }) {
  return (
    <>
      <div className="flex justify-between border-b-2 border-[#14110d] pb-1.5 mb-3" style={{ flexWrap: 'nowrap' }}>
        <span className={`${FUENTE_ROTULO} text-xs text-[#3b342a]`}>Pregunta {paso} de 3</span>
        <span className={`${FUENTE_ROTULO} text-xs text-[#3b342a]`}>Criterio ACSM</span>
      </div>
      <p className={`${FUENTE_TITULOS} text-2xl sm:text-3xl leading-tight text-[#14110d]`}>{titulo}</p>
      {ayuda && <p className="mt-1.5 text-sm text-[#4a4333]">{ayuda}</p>}
    </>
  );
}

export default function ProbarPage() {
  const navigate = useNavigate();
  const [paso, setPaso] = useState(1); // 1, 2, 3 y 4 = resultado
  const [datos, setDatos] = useState({});
  const [activo, setActivo] = useState(null); // true | false | null

  useEffect(() => {
    try { trackViewContent({ content_name: 'Probar 3 preguntas', value: PACK_PRICE_ARS, content_ids: ['pack'] }); } catch (_) { /* noop */ }
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [paso]);

  const alternar = (clave) => setDatos((d) => ({ ...d, [clave]: !d[clave] }));
  const algunoDe = (lista) => lista.some(([k]) => datos[k] === true);
  const ninguno = (lista) => setDatos((d) => ({ ...d, ...Object.fromEntries(lista.map(([k]) => [k, false])) }));

  // Mismo criterio que el paso de actividad de la entrevista: "activo regular"
  // solo si cumple los cuatro criterios; si no, cuenta como inactivo.
  const formData = { ...datos, activity_level: activo ? 'activo_regular' : 'sedentario' };
  const resultado = paso === 4 ? evaluateDecisionFE(formData) : null;
  const presentacion = resultado ? getDecisionPresentation(resultado.estado) : null;

  const comprarPack = () => {
    try {
      trackInitiateCheckout({ content_name: `Pack de ${PACK_ENTREVISTAS} entrevistas`, value: PACK_PRICE_ARS, content_type: 'product', content_ids: ['pack'] });
    } catch (_) { /* noop */ }
    navigate('/checkout?producto=pack');
  };

  const reiniciar = () => {
    setDatos({});
    setActivo(null);
    setPaso(1);
  };

  return (
    <div className="min-h-screen min-h-svh w-full overflow-x-hidden relative isolate" style={{ backgroundColor: '#0b0b0c', fontFamily: FUENTE_TEXTO }}>
      <div className="absolute inset-0 -z-10" style={{ backgroundImage: GRANO }} aria-hidden="true" />
      <div
        className="absolute inset-0 -z-10 bg-[radial-gradient(circle,rgba(255,255,255,0.07)_1px,transparent_1.5px)] [background-size:7px_7px]"
        aria-hidden="true"
      />

      <header className="px-4 h-16 flex items-center justify-between max-w-3xl mx-auto">
        <a href="/" aria-label="Ir a EVALPRO">
          <img src="/landing/logo-evalpro-claro.png" alt="EVALPRO" className="h-9 w-auto" />
        </a>
        <img src="/landing/logo-efs-claro.png" alt="Ejercicio Físico & Salud" className="h-10 w-auto" />
      </header>

      <main className="px-4 pb-16 max-w-3xl mx-auto" data-testid="probar-page">
        {paso < 4 && (
          <div className="pt-4 sm:pt-8 text-center sm:text-left">
            <p className={`${FUENTE_ROTULO} text-sm text-[#8CF0B4] [text-shadow:0_1px_3px_rgba(0,0,0,0.9)]`}>Para profes y gimnasios</p>
            <h1
              className={`${FUENTE_TITULOS} mt-2 text-[#efe6d2] leading-[1.04] [text-shadow:0_3px_0_rgba(0,0,0,0.85)]`}
              style={{ fontSize: 'clamp(2rem, 6.5vw, 3.4rem)' }}
              data-testid="probar-title"
            >
              ¿Con qué intensidad <span className={RESALTADO}>real</span> entrena tu alumno?
            </h1>
            <p className="mt-3 text-[#d9cfb6] text-base sm:text-lg leading-relaxed">
              Pensá en un alumno tuyo, nuevo o de siempre, y respondé 3 preguntas. Es el algoritmo
              de screening del ACSM, el mismo que usa EVALPRO.
            </p>
          </div>
        )}

        <div className="relative mt-8">
          <Mancha className="-inset-x-3 -inset-y-4 sm:-inset-x-6 sm:-inset-y-6" />

          {/* Mismo orden que la entrevista de la app: actividad, enfermedades,
              signos y síntomas. El orden no cambia el resultado. */}
          {paso === 1 && (
            <Papel semilla={41} giro={-0.3} className="p-5 sm:p-7" data-testid="probar-paso-1">
              <Cabecera paso={1} titulo="¿Hace ejercicio planificado de forma regular?" />
              <ul className="mt-3 space-y-1 text-[15px] text-[#2a241b]">
                {[
                  'De intensidad moderada o más',
                  'Al menos 30 minutos por sesión',
                  'Al menos 3 veces por semana',
                  'Desde hace 3 meses o más',
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2" style={{ flexWrap: 'nowrap' }}>
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0 text-[#27AE60]" strokeWidth={4} /> {t}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-sm text-[#4a4333]">Si no cumple las cuatro, la respuesta es no.</p>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <Opcion activa={activo === true} onClick={() => setActivo(true)} testId="probar-activo-si">Sí, cumple las cuatro</Opcion>
                <Opcion activa={activo === false} onClick={() => setActivo(false)} testId="probar-activo-no">No</Opcion>
              </div>
              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => setPaso(2)}
                  disabled={activo === null}
                  className={`${BTN_PRIMARY} h-12 w-full sm:w-auto disabled:opacity-50`}
                  data-testid="probar-siguiente-1"
                >
                  Siguiente <ArrowRight className="hidden sm:block h-4 w-4 ml-2" />
                </Button>
              </div>
            </Papel>
          )}

          {paso === 2 && (
            <Papel semilla={42} giro={0.3} className="p-5 sm:p-7" data-testid="probar-paso-2">
              <Cabecera
                paso={2}
                titulo="¿Tiene alguna de estas enfermedades diagnosticadas?"
                ayuda="La hipertensión y el colesterol se registran en la entrevista completa, pero no cambian este resultado."
              />
              <div className="mt-4 grid gap-2">
                {ENFERMEDADES.map(([k, t]) => (
                  <Opcion key={k} activa={datos[k] === true} onClick={() => alternar(k)} testId={`probar-${k}`}>{t}</Opcion>
                ))}
              </div>
              <div className="mt-5 flex flex-col-reverse sm:flex-row gap-3">
                <Button onClick={() => setPaso(1)} className={`${BTN_SECUNDARIO} h-12 w-full sm:w-auto !text-[#14110d] !border-[#14110d]/40`}>
                  <ArrowLeft className="h-4 w-4 mr-2" /> Atrás
                </Button>
                {algunoDe(ENFERMEDADES) ? (
                  <Button onClick={() => setPaso(3)} className={`${BTN_PRIMARY} h-12 w-full sm:w-auto`} data-testid="probar-siguiente-2">
                    Siguiente <ArrowRight className="hidden sm:block h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button onClick={() => { ninguno(ENFERMEDADES); setPaso(3); }} className={`${BTN_PRIMARY} h-12 w-full sm:w-auto`} data-testid="probar-ninguno-2">
                    No tiene ninguna <ArrowRight className="hidden sm:block h-4 w-4 ml-2" />
                  </Button>
                )}
              </div>
            </Papel>
          )}

          {paso === 3 && (
            <Papel semilla={43} giro={-0.3} className="p-5 sm:p-7" data-testid="probar-paso-3">
              <Cabecera paso={3} titulo="¿Tiene alguno de estos signos o síntomas?" ayuda="Marcá todos los que tenga." />
              <div className="mt-4 grid gap-2">
                {SINTOMAS.map(([k, t]) => (
                  <Opcion key={k} activa={datos[k] === true} onClick={() => alternar(k)} testId={`probar-${k}`}>{t}</Opcion>
                ))}
              </div>
              <div className="mt-5 flex flex-col-reverse sm:flex-row gap-3">
                <Button onClick={() => setPaso(2)} className={`${BTN_SECUNDARIO} h-12 w-full sm:w-auto !text-[#14110d] !border-[#14110d]/40`}>
                  <ArrowLeft className="h-4 w-4 mr-2" /> Atrás
                </Button>
                {algunoDe(SINTOMAS) ? (
                  <Button onClick={() => setPaso(4)} className={`${BTN_PRIMARY} h-12 w-full sm:w-auto`} data-testid="probar-ver-resultado">
                    Ver el resultado <ArrowRight className="hidden sm:block h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button onClick={() => { ninguno(SINTOMAS); setPaso(4); }} className={`${BTN_PRIMARY} h-12 w-full sm:w-auto`} data-testid="probar-ninguno-3">
                    No tiene ninguno · ver el resultado <ArrowRight className="hidden sm:block h-4 w-4 ml-2" />
                  </Button>
                )}
              </div>
            </Papel>
          )}

          {paso === 4 && presentacion && (
            <div className="grid gap-4" data-testid="probar-resultado">
              <Papel semilla={44} giro={-0.4} className="p-5 sm:p-7">
                <div className="flex justify-between border-b-2 border-[#14110d] pb-1.5 mb-3" style={{ flexWrap: 'nowrap' }}>
                  <span className={`${FUENTE_ROTULO} text-xs text-[#3b342a]`}>Resultado · Criterio ACSM</span>
                  <span className={`${FUENTE_ROTULO} text-xs text-[#3b342a]`}>EVALPRO</span>
                </div>
                <p className={`${FUENTE_ROTULO} text-sm text-[#3b342a]`}>Cómo arranca tu alumno</p>
                <div className="mt-1 flex items-center gap-3" style={{ flexWrap: 'nowrap' }}>
                  <span
                    className="h-5 w-5 rounded-full flex-shrink-0 border-2 border-[#14110d]"
                    style={{ backgroundColor: COLOR_ESCENARIO[presentacion.bannerColor] }}
                    aria-hidden="true"
                  />
                  <p className={`${FUENTE_TITULOS} text-3xl sm:text-5xl leading-none text-[#14110d]`} data-testid="probar-escenario">
                    {presentacion.headline}
                  </p>
                </div>
                <dl className="mt-4 grid gap-1.5 text-[15px]">
                  <div className="flex gap-2" style={{ flexWrap: 'nowrap' }}>
                    <dt className="font-bold w-24 flex-shrink-0">Moderada:</dt>
                    <dd>{presentacion.moderate}</dd>
                  </div>
                  <div className="flex gap-2" style={{ flexWrap: 'nowrap' }}>
                    <dt className="font-bold w-24 flex-shrink-0">Vigorosa:</dt>
                    <dd>{presentacion.vigorous}</dd>
                  </div>
                </dl>
                <p className="mt-4 text-[15px] leading-relaxed text-[#2a241b]">
                  {resultado.conducta_override || presentacion.conducta}
                </p>
                <p className="mt-3 text-sm text-[#4a4333]">Por qué: {(resultado.motivos_decision || []).join(' ')}</p>
              </Papel>

              <Papel semilla={45} giro={0.4} className="p-5 sm:p-7" data-testid="probar-oferta">
                <p className={`${FUENTE_TITULOS} text-2xl sm:text-3xl leading-tight text-[#14110d]`}>
                  Esto es la base. La entrevista completa te da el resto.
                </p>
                <ul className="mt-3 space-y-1.5 text-[15px] text-[#2a241b]">
                  {[
                    'Informe en PDF con la conducta sugerida y el porqué',
                    'Zonas de frecuencia cardíaca y ficha de cada alumno',
                    'Retest cada 3 meses, sin gastar entrevistas',
                    'Pruebas funcionales: handgrip, caminata de 6 minutos, sentarse-pararse y escalón',
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2" style={{ flexWrap: 'nowrap' }}>
                      <Check className="h-4 w-4 mt-0.5 flex-shrink-0 text-[#27AE60]" strokeWidth={4} /> {t}
                    </li>
                  ))}
                </ul>
                <p className={`${FUENTE_TITULOS} mt-5 text-2xl sm:text-3xl text-[#14110d]`}>
                  Probalo con {PACK_ENTREVISTAS} alumnos por {formatPriceARS(PACK_PRICE_ARS)}
                </p>
                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                  <Button onClick={comprarPack} className={`${BTN_PRIMARY} h-12 w-full sm:w-auto`} data-testid="probar-comprar-pack">
                    Comprar el pack <ArrowRight className="hidden sm:block h-4 w-4 ml-2" />
                  </Button>
                  <Button
                    onClick={() => navigate('/evaluate')}
                    className={`${BTN_SECUNDARIO} h-12 w-full sm:w-auto !text-[#14110d] !border-[#14110d]/40`}
                    data-testid="probar-entrevista-completa"
                  >
                    Hacer la entrevista completa gratis
                  </Button>
                </div>
                <p className="mt-3 text-xs text-[#4a4333]">
                  Si después pasás al ilimitado, te descontamos lo que pagaste.
                </p>
              </Papel>

            </div>
          )}
        </div>

        {paso === 4 && (
          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={reiniciar}
              className={`${FUENTE_ROTULO} inline-flex items-center gap-2 text-sm text-[#d9cfb6] hover:text-white`}
              data-testid="probar-reiniciar"
            >
              <RotateCcw className="h-4 w-4" /> Probar con otro alumno
            </button>
          </div>
        )}

        <p className={`${FUENTE_ROTULO} mt-10 text-center text-[11px] leading-relaxed text-[#a9a08c]`}>
          Basado en el algoritmo de screening del Colegio Americano de Medicina del Deporte (Riebe et al., 2015).
          Sugerencia de screening. No reemplaza el apto médico ni al médico.
        </p>
      </main>
    </div>
  );
}
